import { useContext, useMemo } from 'react';
import { getChartDefinition, type ChartId } from '../catalog/chartCatalog';
import type {
  AnalyticsChart,
  BuildChartContext,
  ChartData,
  ChartDefinition,
  ChartResult,
} from '../catalog/types';
import { ChartDetailsContext } from '../context/chartDetailsContext';
import { buildChartData } from '../model/buildChartData';
import { useAnalyticsChart, useChartResult } from './useAnalyticsChart';
import { useBuildChartContext } from './useBuildChartContext';

export type CatalogChartSource = Readonly<{
  isDetailedView: boolean;
  ctx: BuildChartContext;
  chart: AnalyticsChart | undefined;
  result: ChartResult | undefined;
  definition: ChartDefinition | undefined;
}>;

export type CatalogChartData = CatalogChartSource &
  Readonly<{
    data: ChartData | undefined;
  }>;

/** What the catalog chart data is built from; cheap, for callers that build it only when needed. */
export function useCatalogChartSource(chartId: ChartId): CatalogChartSource {
  const isDetailedView = useContext(ChartDetailsContext);
  const ctx = useBuildChartContext(isDetailedView ? 'details' : 'dashboard');
  const chart = useAnalyticsChart(chartId);
  const result = useChartResult(chartId);
  const definition = getChartDefinition(chartId);

  return { isDetailedView, ctx, chart, result, definition };
}

/** Resolves catalog chart data (translated labels) for rendering. */
export function useCatalogChartData(chartId: ChartId): CatalogChartData {
  const source = useCatalogChartSource(chartId);
  const { definition, result, ctx } = source;
  const data = useMemo(
    () =>
      definition && result ? buildChartData(definition, result, ctx) : undefined,
    [definition, result, ctx]
  );

  return { ...source, data };
}
